// Singleton loader for the Google Places library (Places API "New").
//
// We use the NEW `AutocompleteSuggestion` API + a custom dropdown rather than
// the legacy `google.maps.places.Autocomplete` widget: the legacy widget is not
// available to projects created after March 2025 (it returns
// LegacyApiNotActivatedMapError when only "Places API (New)" is enabled, which
// is this project's config). The new API returns predictions programmatically,
// so we render our own brand-styled dropdown and keep the structured-fill flow.
//
// Loads the Maps JS bootstrap once and resolves the `places` library via
// importLibrary. Graceful fallback: if the env key is absent or the script
// fails, resolves to `null` and callers render a plain input with no
// autocomplete and no error shown to the user.
//
// s227 (D9 part 2, widened): the fallback is no longer SILENT to a developer. Staging ran for weeks with no key,
// then with a key Google refused (403 API_KEY_HTTP_REFERRER_BLOCKED), and both looked exactly like "the lookup
// was never built". Each cause now writes ONE clear console warning (`warnPlaces`): the key is missing, Google
// refused the key, or the library did not load. Users see nothing new. Never logs what the planner typed.

// Minimal structural typing — we avoid adding @types/google.maps (a dependency
// bump is risky on Windows per the lockfile gotcha). Callers treat the library
// as opaque. The shape we use: { AutocompleteSuggestion, AutocompleteSessionToken }.
type PlacesLibrary = any;

const SCRIPT_ID = "google-maps-places";

export type PlacesProblem = "missing-key" | "key-refused" | "load-failed";

const PLACES_WARNINGS: Record<PlacesProblem, string> = {
  "missing-key":
    "[address lookup] VITE_GOOGLE_PLACES_API_KEY is not set in this build, so address suggestions are off. Add the key to this environment's build variables and redeploy.",
  "key-refused":
    "[address lookup] Google refused the Places API key, so address suggestions are off. Check the key's allowed websites (HTTP referrers) and allowed APIs in Google Cloud.",
  "load-failed":
    "[address lookup] The Google Places library did not load, so address suggestions are off.",
};

const warned = new Set<PlacesProblem>();

/** One console warning per cause per page load. `detail` is Google's own reason (never user input). */
export function warnPlaces(problem: PlacesProblem, detail?: string): void {
  if (warned.has(problem)) return;
  warned.add(problem);
  // eslint-disable-next-line no-console
  console.warn(detail ? `${PLACES_WARNINGS[problem]} Google said: ${detail}` : PLACES_WARNINGS[problem]);
}

/** Google's reason, from a rejected Places call: the error's own message, trimmed. Pure. */
export function googleReason(err: unknown): string {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return msg.replace(/AIza[0-9A-Za-z_-]+/g, "[key]").slice(0, 300);
}

/**
 * A Places call failed. A refusal of the KEY (403, API_KEY_*, referer or permission) is reported once; any other
 * failure (the network, a busy server) is left to the caller's quiet fallback, as before.
 */
export function reportPlacesFailure(err: unknown): void {
  const reason = googleReason(err);
  if (/API_KEY|REFERRER|referer|PERMISSION_DENIED|403|not authorized|RequestDenied/i.test(reason)) warnPlaces("key-refused", reason);
}

/** Test hook: forget which warnings were written. */
export function resetPlacesWarnings(): void {
  warned.clear();
}

let loadPromise: Promise<PlacesLibrary | null> | null = null;

export function loadPlacesLibrary(): Promise<PlacesLibrary | null> {
  if (loadPromise) return loadPromise;

  if (typeof window === "undefined") {
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }

  const key = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined;
  if (!key) {
    // No key configured — plain inputs for the user; one warning for the developer (s227, D9 part 2).
    warnPlaces("missing-key");
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }

  // Maps JS calls this global when it rejects the key (e.g. the site is not on the key's allowed list).
  (window as any).gm_authFailure = () => warnPlaces("key-refused", "Maps JavaScript API authentication failed (gm_authFailure).");

  loadPromise = new Promise<PlacesLibrary | null>((resolve) => {
    // Resolve as soon as the async bootstrap exposes importLibrary. We do NOT
    // rely on the <script>'s onload event: with loading=async the bootstrap
    // defines google.maps.importLibrary independently of (and not reliably
    // before) onload, so onload-gating can leave this promise pending forever
    // while importLibrary is in fact ready. Poll for it instead.
    const tryResolve = (): boolean => {
      const maps = (window as any).google?.maps;
      if (maps?.importLibrary) {
        maps
          .importLibrary("places")
          .then((lib: PlacesLibrary) => resolve(lib ?? null))
          .catch((err: unknown) => { warnPlaces("load-failed", googleReason(err)); resolve(null); });
        return true;
      }
      return false;
    };

    // Already available (HMR re-mount, or a prior caller injected the bootstrap).
    if (tryResolve()) return;

    // Inject the bootstrap once.
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        key,
      )}&libraries=places&loading=async&v=weekly`;
      script.async = true;
      document.head.appendChild(script);
    }

    // Poll for importLibrary availability; give up after a timeout and fall back
    // to a plain input (graceful — no error surfaced to the user).
    let waited = 0;
    const interval = setInterval(() => {
      if (tryResolve()) {
        clearInterval(interval);
        return;
      }
      waited += 100;
      if (waited >= 10000) {
        clearInterval(interval);
        loadPromise = null; // allow a future retry on the next mount
        warnPlaces("load-failed", "the Maps script did not become ready within 10 seconds.");
        resolve(null);
      }
    }, 100);
  });

  return loadPromise;
}

/**
 * Parse a Place (Places API New) into our structured AddressData shape. Keeps
 * `street2` (unit/suite) from the previous value — Google doesn't reliably
 * return a subpremise and the user typed it themselves. City falls back through
 * the various locality component types international results use.
 *
 * The new API exposes `place.addressComponents` with `longText` / `shortText`
 * (camelCase), unlike the legacy `long_name` / `short_name`.
 */
export function placeToAddressParts(place: any): {
  street: string;
  city: string;
  state: string;
  postal: string;
  country: string;
} {
  const components: any[] = place?.addressComponents ?? [];
  const get = (type: string): string => {
    const c = components.find((comp) => comp.types?.includes(type));
    return c?.longText ?? "";
  };
  const getShort = (type: string): string => {
    const c = components.find((comp) => comp.types?.includes(type));
    return c?.shortText ?? "";
  };

  const streetNumber = get("street_number");
  const route = get("route");
  const city =
    get("locality") ||
    get("postal_town") ||
    get("sublocality") ||
    get("administrative_area_level_2") ||
    "";

  return {
    street: [streetNumber, route].filter(Boolean).join(" "),
    city,
    state: getShort("administrative_area_level_1"),
    postal: get("postal_code"),
    country: get("country"),
  };
}
